/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { type ChangeEvent, useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Form,
  FormGroup,
  Label,
  Spinner,
} from 'reactstrap';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import { createGlobalStyle } from 'styled-components';
import { toast } from 'react-toastify';
import { api } from '../../../services/api';

import 'react-datepicker/dist/react-datepicker.css';
import LimitedTextarea from '../LimitedTextArea';

registerLocale('ptBR', ptBR);

const DatePickerWrapperStyles = createGlobalStyle`
  .date_picker.full-width {
    width: 100%;
  }
  .react-datepicker-popper {
    z-index: 9999 !important;
  }
`;

interface NotificationType {
  id: string;
  titulo: string;
  conteudo: string;
  data_hora: string;
  tipo_notificacao: 'popup' | 'push';
  texto_popup?: string;
  upload_image?: string;
  status: string;
  createdAt: string;
}

interface Props {
  isOpen: boolean;
  handleIsOpen: () => void;
  notification: NotificationType;
  getAllNotifications: (page: number) => Promise<void>;
}

export function ModalEditNotification({
  isOpen,
  handleIsOpen,
  notification,
  getAllNotifications,
}: Props): React.JSX.Element {
  const [title, setTitle] = useState('');
  const [shouldRemoveImage, setShouldRemoveImage] = useState(false);
  const [content, setContent] = useState('');
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [scheduleTime, setScheduleTime] = useState('');
  const [notificationType, setNotificationType] = useState<'push' | 'popup'>('push');
  const [file, setFile] = useState<File | null>(null);
  const [popupText, setPopupText] = useState('');
  const [status, setStatus] = useState('');
  const [currentImage, setCurrentImage] = useState<string>('');

  const [isOpenConfirmDeleteImage, setIsOpenConfirmDeleteImage] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorContent, setErrorContent] = useState('');
  const [errorScheduleDate, setErrorScheduleDate] = useState('');
  const [errorScheduleTime, setErrorScheduleTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSameDate, setIsSameDate] = useState(false);

  const isDisabled = notification.status === 'Enviado' || notification.status === 'Cancelado';

  useEffect(() => {
    if (notification) {
      setTitle(notification.titulo);
      setContent(notification.conteudo);
      setNotificationType(notification.tipo_notificacao);
      setPopupText(notification.texto_popup || '');
      setStatus(notification.status);
      setCurrentImage(notification.upload_image || '');

      // Parse do created_at para data local
      const createdAtLocal = new Date(notification.createdAt);

      // Parse do data_hora manualmente
      const [datePart, timePart] = notification.data_hora.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);
      const dataHora = new Date(year, month - 1, day, hour, minute);
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      const isSameDate = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate() &&
        a.getHours() === b.getHours() &&
        a.getMinutes() === b.getMinutes();

      const sameDay = isSameDate(createdAtLocal, dataHora);

      if (sameDay) {
        setIsSameDate(sameDay);
      }

      if (notification.data_hora) {
        const [datePart, timePart] = notification.data_hora.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
        const date = new Date(year, month - 1, day, hour, minute);

        setScheduleDate(date);
        setScheduleTime(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
      }
    }
  }, [hasInitialized, isOpen]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0] ?? null;
    setFile(selectedFile);
    if (selectedFile) {
      setCurrentImage('');
    }
  };

  const handleSubmit = async (): Promise<void> => {
    let hasError = false;

    if (!title) {
      setErrorTitle('Campo obrigatório.');
      hasError = true;
    } else {
      setErrorTitle('');
    }

    if (!content && notificationType === 'push') {
      setErrorContent('Campo obrigatório.');
      hasError = true;
    } else {
      setErrorContent('');
    }

    if (status === 'Agendado') {
      if (!scheduleDate) {
        setErrorScheduleDate('Campo obrigatório.');
        hasError = true;
      } else {
        setErrorScheduleDate('');
      }

      if (!scheduleTime) {
        setErrorScheduleTime('Campo obrigatório.');
        hasError = true;
      } else {
        setErrorScheduleTime('');
      }
    }

    if (hasError) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('titulo', title);
      if (notificationType === 'push') {
        formData.append('conteudo', content);
      }
      // **Não append data_hora se status for 'Enviado'**
      if (status !== 'Enviado') {
        let formattedDate = notification.data_hora;

        if (scheduleDate && scheduleTime) {
          const dateToSend = new Date(
            scheduleDate.setHours(
              Number(scheduleTime.split(':')[0]),
              Number(scheduleTime.split(':')[1]),
            ),
          );

          formattedDate = `${dateToSend.getFullYear()}-${String(dateToSend.getMonth() + 1).padStart(
            2,
            '0',
          )}-${String(dateToSend.getDate()).padStart(2, '0')} ${String(
            dateToSend.getHours(),
          ).padStart(2, '0')}:${String(dateToSend.getMinutes()).padStart(2, '0')}`;
        }

        formData.append('data_hora', formattedDate || '');
      }

      formData.append('tipo_notificacao', notificationType);
      formData.append('status', status);

      if (notificationType === 'popup') {
        formData.append('texto_popup', popupText || '');

        if (file) {
          formData.append('file', file);
        }

        if (!file && !currentImage) {
          formData.append('remove_image', 'true');
        }
      }

      if (shouldRemoveImage) {
        try {
          await api.delete(`/notifications/${notification.id}/file`);
        } catch (err) {
          console.error('Erro ao remover imagem:', err);
          toast.error('Erro ao remover imagem.');
        }
      }

      await api.patch(`/notifications/${notification.id}`, formData);

      toast.success('Notificação atualizada com sucesso!');
      handleClose();
      await getAllNotifications(1);
    } catch (error) {
      toast.error('Erro ao atualizar notificação!');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleConfirmDeleteImage = (): void => {
    setIsOpenConfirmDeleteImage(!isOpenConfirmDeleteImage);
  };

  const handleClose = (): void => {
    setHasInitialized(false);
    setTitle('');
    setContent('');
    setScheduleDate(null);
    setScheduleTime('');
    setNotificationType('push');
    setFile(null);
    setPopupText('');
    setCurrentImage('');
    setShouldRemoveImage(false);

    setErrorTitle('');
    setErrorContent('');
    setErrorScheduleDate('');
    setErrorScheduleTime('');

    handleIsOpen();
  };

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setter(e.target.value);
    };

  useEffect(() => {
    console.log('status atual:', `"${status}"`, status === 'Enviado');
  }, [status]);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>Editar Notificação</ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label style={{ color: errorTitle ? '#E44B39' : undefined }}>Título</Label>
            <Input
              value={title}
              onChange={handleInputChange(setTitle)}
              style={{ borderColor: errorTitle ? '#E44B39' : undefined }}
              disabled={isDisabled}
            />
            {errorTitle && <div style={{ color: '#E44B39' }}>{errorTitle}</div>}
          </FormGroup>
          {notificationType === 'push' && (
            <FormGroup>
              <Label style={{ color: errorContent ? '#E44B39' : undefined }}>Conteúdo</Label>
              {/* <Input
              type="textarea"
              value={content}
              onChange={handleInputChange(setContent)}
              style={{ borderColor: errorContent ? '#E44B39' : undefined }}
              disabled={isDisabled}
            /> */}
              <LimitedTextarea
                value={content}
                onChange={setContent}
                placeholder="Conteúdo da notificação"
                error={Boolean(errorContent)}
                disabled={isDisabled}
              />
              {errorContent && <div style={{ color: '#E44B39' }}>{errorContent}</div>}
            </FormGroup>
          )}
          {notification.status === 'Cancelado' || notification.status === 'Enviado' ? (
            <FormGroup tag="fieldset">
              <Label>Tipo de envio</Label>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    checked={notification.status === 'Enviado'}
                    disabled={isDisabled}
                  />{' '}
                  Notificar agora
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    checked={notification.status !== 'Enviado'}
                    disabled={isDisabled}
                  />{' '}
                  Agendar notificação
                </Label>
              </FormGroup>
            </FormGroup>
          ) : (
            <FormGroup tag="fieldset">
              <Label>Tipo de envio</Label>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    checked={status === 'Enviado'}
                    onChange={() => setStatus('Enviado')}
                    disabled={isDisabled}
                  />{' '}
                  Notificar agora
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    checked={status === 'Agendado'}
                    onChange={() => setStatus('Agendado')}
                    disabled={isDisabled}
                  />{' '}
                  Agendar notificação
                </Label>
              </FormGroup>
            </FormGroup>
          )}

          {status === 'Agendado' && (
            <>
              <FormGroup>
                <Label style={{ color: errorScheduleDate ? '#E44B39' : undefined }}>Data</Label>
                <ReactDatePicker
                  selected={scheduleDate}
                  onChange={setScheduleDate}
                  wrapperClassName="date_picker full-width"
                  popperClassName="react-datepicker-popper"
                  dateFormat="dd/MM/yyyy"
                  locale="ptBR"
                  customInput={<Input disabled={isDisabled} />}
                  minDate={new Date()}
                  disabled={isDisabled}
                />
                <DatePickerWrapperStyles />
                {errorScheduleDate && <div style={{ color: '#E44B39' }}>{errorScheduleDate}</div>}
              </FormGroup>

              <FormGroup>
                <Label style={{ color: errorScheduleTime ? '#E44B39' : undefined }}>Hora</Label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={handleInputChange(setScheduleTime)}
                  style={{ borderColor: errorScheduleTime ? '#E44B39' : undefined }}
                  disabled={isDisabled}
                />
                {errorScheduleTime && <div style={{ color: '#E44B39' }}>{errorScheduleTime}</div>}
              </FormGroup>
            </>
          )}

          <FormGroup tag="fieldset">
            <Label>Tipo de notificação</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  checked={notificationType === 'push'}
                  onChange={() => setNotificationType('push')}
                  disabled={isDisabled}
                />{' '}
                Push
              </Label>
            </FormGroup>
            <FormGroup check>
              <Label check>
                <Input
                  type="radio"
                  checked={notificationType === 'popup'}
                  onChange={() => setNotificationType('popup')}
                  disabled={isDisabled}
                />{' '}
                Pop-up
              </Label>
            </FormGroup>
          </FormGroup>

          {notificationType === 'popup' && (
            <FormGroup>
              <Label>Imagem (opcional)</Label>

              {currentImage && !file ? (
                <div style={{ marginBottom: 10 }}>
                  <img
                    src={currentImage}
                    alt="Imagem atual"
                    style={{ width: '50%', borderRadius: 8, marginBottom: 10 }}
                  />
                  {!isDisabled && (
                    <div>
                      <Button color="danger" size="sm" onClick={toggleConfirmDeleteImage}>
                        Remover imagem
                      </Button>
                    </div>
                  )}
                </div>
              ) : file ? (
                <div style={{ marginBottom: 10 }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt="Preview"
                    style={{ width: '50%', borderRadius: 8, marginBottom: 10 }}
                  />
                  {!isDisabled && (
                    <div>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => {
                          setFile(null);
                          setCurrentImage('');
                          toggleConfirmDeleteImage();
                        }}
                      >
                        Remover
                      </Button>
                    </div>
                  )}
                </div>
              ) : null}

              {!isDisabled && <Input type="file" accept="image/*" onChange={handleFileChange} />}

              <Label className="mt-3">Ou texto exibido após login</Label>
              {/* <Input
                type="textarea"
                value={popupText}
                onChange={handleInputChange(setPopupText)}
                placeholder="Texto de exibição"
                disabled={isDisabled}
              /> */}
              <LimitedTextarea
                value={popupText}
                onChange={setPopupText}
                placeholder="Texto de exibição"
                disabled={isDisabled}
              />
            </FormGroup>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        {notification.status === 'Agendado' && (
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
            style={{ minWidth: 120 }}
          >
            {loading ? <Spinner size="sm" /> : 'Atualizar'}
          </Button>
        )}
        <Button color="secondary" onClick={handleClose}>
          Cancelar
        </Button>
      </ModalFooter>

      {isOpenConfirmDeleteImage && (
        <Modal isOpen={isOpenConfirmDeleteImage} toggle={toggleConfirmDeleteImage}>
          <ModalHeader toggle={toggleConfirmDeleteImage}>Remover imagem</ModalHeader>
          <ModalBody>Tem certeza que deseja remover essa imagem?</ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => {
                setFile(null);
                setCurrentImage('');
                setShouldRemoveImage(true);
                toggleConfirmDeleteImage();
              }}
            >
              Remover
            </Button>
            <Button color="secondary" onClick={toggleConfirmDeleteImage}>
              Cancelar
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Modal>
  );
}
